import { defineNuxtPlugin } from "#app";
import type { Directive } from "vue";
import type { Instance as PopperInstance, Placement } from "@popperjs/core";
import { createPopper } from "@popperjs/core";

/*
 * States
 */

interface TooltipState {
  popper: PopperInstance
  el: HTMLElement
  show: () => void
  hide: () => void
  update: (text: string, placement: Placement) => void
  destroy: () => void
}

/*
 * Implementation
 */

// Each element’s state is stored here → no need to augment HTMLElement
const states = new WeakMap<HTMLElement, TooltipState>()

const mouseEnterEvents: Array<keyof HTMLElementEventMap> = ['mouseenter', 'focus']
const mouseLeaveEvents: Array<keyof HTMLElementEventMap> = ['mouseleave', 'blur']

function createState (
  target: HTMLElement,
  text: string,
  placement: Placement
): TooltipState {
  const tooltipEl = document.createElement('div')
  tooltipEl.className =
    'v-tooltip tooltip-content transition-opacity z-[1051] text-white paper-shadow bg-paper-black rounded px-3 py-2.5 text-sm'
  tooltipEl.textContent = text
  document.body.appendChild(tooltipEl)

  const arrowEl = document.createElement('div')
  arrowEl.className = 'tooltip-arrow bg-paper-black'
  arrowEl.setAttribute('data-popper-arrow', '')
  tooltipEl.appendChild(arrowEl)

  /* Popper */
  const popper = createPopper(target, tooltipEl, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'arrow', options: { element: arrowEl } }
    ]
  })

  /* API */
  const state: TooltipState = {
    popper,
    el: tooltipEl,

    show () {
      tooltipEl.setAttribute('data-show', '')
    },

    hide () {
      tooltipEl.removeAttribute('data-show')
    },

    update (newText, newPlacement) {
      if (tooltipEl.textContent !== newText) {
        tooltipEl.textContent = newText
      }
      popper.setOptions(opts => ({ ...opts, placement: newPlacement }))
      popper.update()
    },

    destroy () {
      popper.destroy()
      tooltipEl.remove()
    }
  }

  return state
}

/*
 * Utility functions
 */

const isTruncated = (el: HTMLElement): boolean =>
  el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight

const addListeners = (el: HTMLElement, state: TooltipState): void => {
  mouseEnterEvents.forEach(evt => el.addEventListener(evt, state.show))
  mouseLeaveEvents.forEach(evt => el.addEventListener(evt, state.hide))
}

const removeListeners = (el: HTMLElement, state: TooltipState): void => {
  mouseEnterEvents.forEach(evt => el.removeEventListener(evt, state.show))
  mouseLeaveEvents.forEach(evt => el.removeEventListener(evt, state.hide))
}

/*
 * Directives
 */

interface FactoryOptions {
  truncatedOnly: boolean
}

function makeDirective (
  { truncatedOnly }: FactoryOptions
): Directive<HTMLElement, string> {
  return {
    mounted (el, binding) {
      if (truncatedOnly && !isTruncated(el)) return
      const placement = (binding.arg as Placement | undefined) ?? 'top'
      const text = truncatedOnly ? el.textContent ? el.textContent : '' : binding.value;
      const state = createState(el, text, placement)
      states.set(el, state)
      addListeners(el, state)
    },

    updated (el, binding) {
      const placement = (binding.arg as Placement | undefined) ?? 'top'
      const state = states.get(el)
      const text = truncatedOnly ? el.textContent ? el.textContent : '' : binding.value;

      if (truncatedOnly) {
        const nowTruncated = isTruncated(el)

        if (!state && nowTruncated) {
          // became truncated – create tooltip
          const newState = createState(el, text, placement)
          states.set(el, newState)
          addListeners(el, newState)
          return
        }

        if (state && !nowTruncated) {
          // no longer truncated – remove tooltip
          removeListeners(el, state)
          state.destroy()
          states.delete(el)
          return
        }
      }

      if (state) state.update(text, placement)
    },

    beforeUnmount (el) {
      const state = states.get(el)
      if (state) {
        removeListeners(el, state)
        state.destroy()
        states.delete(el)
      }
    },

    getSSRProps: () => ({ show: false })
  }
}

export default defineNuxtPlugin(nuxtApp => {
  nuxtApp.vueApp
    .directive('tooltip', makeDirective({ truncatedOnly: false }))
    .directive('tooltip-on-truncated', makeDirective({ truncatedOnly: true }))
})